<template>
  <div>
    <v-dialog
      v-model="showNewSearchModal"
      :max-width="dialogMaxWidth === undefined ? '55vw' : dialogMaxWidth"
      persistent
      scrollable
      @keydown.esc="closeDialog"
    >
      <v-card class="card-modal">
        <div>
<!--            v-show="hasUnsavedChanges"-->
          <notification-bar
            icon="warning"
            :text="$t('modal.newSearchModal.notificationText')"
            :mainCTAText="$t('modal.newSearchModal.notificationCTA')"
            notification-type="warn"
            :hide-notification="hasUnsavedChanges"
            :component-key="notificationKey"
          ></notification-bar>
          <v-btn class="card-title-close-btn" @click.stop="$emit('close')">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="card-title-container">
          <span>{{ $t('modal.newSearchModal.title') }}</span>
          <span class="heading-2">{{ $t('modal.newSearchModal.subtitle') }}</span>
        </v-card-title>
        <v-card-text>
          <v-layout flex class="option-all-container">
            <div
              class="option-container"
              v-for="option in newSearchOptions"
              :key="option.title"
            >
              <v-layout flex column align-center @click="option.onClickAction" class="option-element">
                <v-spacer></v-spacer>
                  <v-spacer></v-spacer>
                  <img class="option-element-icon" :src="option.imgUrl" :alt="option.imgAlt">
                  <v-spacer></v-spacer>
                <v-spacer></v-spacer>
                <span class="option-title">{{ option.title }}</span>
            </v-layout>
            <span class="option-subtitle">{{ option.subtitle }}</span>
            </div>
          </v-layout>
          <hr id="hr-import-section">
          <v-layout flex column justify-content-start>
            <span class="import-section-toggle" @click="toggleShowImportSection">
              <v-icon v-if="!importSectionOpened" class="import-section-toggle-icon">expand_more</v-icon>
              <v-icon v-else class="import-section-toggle-icon">expand_less</v-icon>
              <span class="heading-2">{{ $t('modal.newSearchModal.importTitle') }}</span>
            </span>
            <Transition name="import-section">
              <div v-if="importSectionOpened" class="body-text import-container">
                <span class="heading-3">{{ $t('modal.newSearchModal.importSubtitle') }}</span>
                <v-layout flex align-center>
                  <v-flex grow>
                    <v-text-field
                      v-model="queryString"
                      :label="$t('misc.exampleQuery')"
                      textarea
                      rows="1"
                      hide-details
                      outline
                    ></v-text-field>
                  </v-flex>
                  <!--  @todo - move two CTA sections to new line if screen too narrow-->
                  <v-flex class="import-section-query-string-cta import-section-query-string-syntax-method">
                    <v-btn-toggle
                      mandatory
                      v-model="querySyntaxMethod"
                      class="query-syntax-btn-group"
                      color="info"
                    >
                      <v-btn class="query-syntax-btn" flat value="polyglot" :color="querySyntaxMethod==='polyglot' ? 'primary' : ''">
                        <span>{{ $t('misc.primarySyntaxMethod') }}</span>
                      </v-btn>
                      <v-btn class="query-syntax-btn" flat value="web" :color="querySyntaxMethod==='web' ? 'primary' : ''">
                        <span>{{ $t('misc.secondarySyntaxMethod') }}</span>
                      </v-btn>
                    </v-btn-toggle>
                  </v-flex>
                  <v-flex class="import-section-query-string-cta">
                    <v-btn class="query-syntax-btn btn-primary" color="primary" @click="$emit('handleSearchStringInput', [queryString, querySyntaxMethod])">
                      <v-icon>arrow_forward</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
                <span class="or-element">{{ $t('misc.orLowerCase') }}</span>
                <drop @drop="handleFileDrop">
                  <v-layout class="upload-file-container">
                    <div class="upload-file-element">
                      <v-icon class="upload-icon">cloud_upload</v-icon>
                    </div>
                    <div class="upload-file-element">
                      <span>{{ $t('modal.newSearchModal.uploadFileLabel') }}</span><br />
                      <span class="text-small" v-html="$t('modal.newSearchModal.fileFormat')"></span>
                    </div>
                    <v-spacer></v-spacer>
                    <div class="upload-file-element">
                      <v-layout flex column>
                        <span class="text-small drag-drop-text" v-html="$t('modal.newSearchModal.dropFilePartOne')"></span>
                        <span class="text-small drag-drop-text drag-drop-text-import-file" @click="handleImportFileClick">{{ $t('modal.newSearchModal.dropFilePartTwo') }}</span>
                      </v-layout>
                    </div>
                  </v-layout>
                </drop>
              </div>
            </Transition>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
    <select-element-modal
      :customShowModal="showTemplateDialog"
      :title="$t('modal.newSearchModal.templateTitle')"
      :confirmButtonText="$t('modal.newSearchModal.templateConfirmBtn')"
      :isTemplateExample="true"
      :listItems="possibleTemplates"
      @close="showTemplateDialog=false"
      @confirmed="confirmSampleSelection"
    >
      <p slot="subtitle">
        <i18n path="modal.newSearchModal.templateSubtitle.text" tag="p">
          <template v-slot:url>
            <a :href="faqUrl" target="_blank">{{ $t('modal.newSearchModal.templateSubtitle.linkText')}}</a>
          </template>
        </i18n>
      </p>
    </select-element-modal>
    <select-element-modal
      :customShowModal="showSourceExampleDialog"
      :title="$t('modal.newSearchModal.sourceTitle')"
      :confirmButtonText="$t('modal.newSearchModal.sourceConfirmBtn')"
      :isSourceExample="true"
      :listItems="possibleEngines"
      @close="showSourceExampleDialog=false"
      @confirmed="confirmSampleSelection"
    >
      <p slot="subtitle">
        <i18n path="modal.newSearchModal.sourceSubtitle.text" tag="p">
          <template v-slot:url>
            <a :href="faqUrl" target="_blank">{{ $t('modal.newSearchModal.sourceSubtitle.linkText')}}</a>
          </template>
        </i18n>
      </p>
    </select-element-modal>
  </div>
</template>

<script>
import {Drop} from 'vue-drag-drop'
import SelectElementModal from '../modals/SelectElementModal'
import NotificationBar from '../parts/NotificationBar'
import { POSSIBLE_ENGINES } from '@/utils/search-engines'
import { POSSIBLE_TEMPLATES } from '@/utils/template-samples'
import '@/style/_variables.scss'
import { faqUrl } from '../../utils/constants'

export default {
  name: 'new-search-modal',
  props: ['queries', 'dialogMaxWidth', 'queryString', 'hasUnsavedChanges'],
  components: {SelectElementModal, Drop, NotificationBar},
  data () {
    return {
      importSectionOpened: false,
      querySyntaxMethod: 'polyglot',
      expandTemplateSection: false,
      showTemplateDialog: false,
      showSourceExampleDialog: false,
      newSearchOptions: [
        {title: this.$t('modal.newSearchModal.options.blankCanvas.title'), subtitle: this.$t('modal.newSearchModal.options.blankCanvas.subtitle'), imgUrl: '../../../static/new-search-canvas/blank-icon.svg', imgAlt: this.$t('modal.newSearchModal.options.blankCanvas.imgAlt'), onClickAction: this.handleOpenBlankCanvasClick},
        {title: this.$t('modal.newSearchModal.options.templates.title'), subtitle: this.$t('modal.newSearchModal.options.templates.subtitle'), imgUrl: '../../../static/new-search-canvas/template-icon.svg', imgAlt: this.$t('modal.newSearchModal.options.templates.imgAlt'), onClickAction: this.handleOpenTemplateClick},
        {title: this.$t('modal.newSearchModal.options.sources.title'), subtitle: this.$t('modal.newSearchModal.options.sources.subtitle'), imgUrl: '../../../static/new-search-canvas/example-icon.svg', imgAlt: this.$t('modal.newSearchModal.options.sources.imgAlt'), onClickAction: this.handleOpenSourceExampleClick},
        {title: this.$t('modal.newSearchModal.options.2dsearchAI.title'), subtitle: this.$t('modal.newSearchModal.options.2dsearchAI.subtitle'), imgUrl: '../../../static/new-search-canvas/ai-icon.svg', imgAlt: this.$t('modal.newSearchModal.options.2dsearchAI.imgAlt'), onClickAction: this.handleOpenGeneratorAIClick}
      ],
      allPossibleEngines: POSSIBLE_ENGINES,
      possibleEngines: null,
      possibleTemplates: POSSIBLE_TEMPLATES,
      templateCanvas: null,
      sourceCanvas: null,
      notificationKey: 0,
      faqUrl: faqUrl
    }
  },
  created () {
    this.possibleEngines = POSSIBLE_ENGINES
    this.possibleEngines.forEach(engine => {
      engine.selected = false
    })
    this.possibleTemplates.forEach(template => {
      template.selected = false
    })
  },
  computed: {
    showNewSearchModal: { get () { return this.$store.state.modalState.showNewSearchModal } }
  },
  methods: {
    closeDialog () {
      this.importSectionOpened = false
      // this.$destroy()  // Possibly needed, tbd
      this.$store.commit('setShowNewSearchModal', false)
    },
    confirmSampleSelection (selectedTemplateId) {
      this.$emit('handleOpenCuratedSearch', selectedTemplateId)
      this.closeDialog()
    },
    toggleShowImportSection () {
      this.importSectionOpened = !this.importSectionOpened
    },
    handleOpenBlankCanvasClick () {
      this.$emit('openBlankCanvas')
    },
    handleOpenTemplateClick () {
      this.showTemplateDialog = true
    },
    handleOpenSourceExampleClick () {
      this.showSourceExampleDialog = true
    },
    handleOpenGeneratorAIClick () {
      this.$store.commit('setShowNewSearchModal', false)
      this.$emit('openGenerator')
    },
    handleImportFileClick () {
      this.closeDialog()
      this.$emit('handleImportFileClick')
    },
    handleFileDrop () {
      this.closeDialog()
      this.$emit('handleFileDrop')
    }
  },
  watch: {
    customShowDialog: function (val, oldVal) {
      if (val === true) {
        this.notificationKey += 1
      }
    }
  }
}
</script>
<style lang="scss" scoped>

.card-title {
  &-container {
    display: flex;
    flex-direction: column;
    position: relative;
  }
  &-close-btn {
    min-width: 50px;
    aspect-ratio: 1;
    position: absolute;
    right: 50px;
    top: 10px;
    box-shadow: unset;
    border-radius: 50%;
  }
}
.option {
  &-all-container {
    overflow-y: auto;
  }
  &-container {
    min-width: 110px;
    width: 23%;
    font-size: 13px;
    margin: 0 10px;
    cursor: pointer;
  }
  &-element {
    border: $length-small-xs solid $black-900;
    border-radius: $length-med-s;
    aspect-ratio: 1;
    margin-bottom: $length-small-m;
    &-icon {
      width: 65%;
      aspect-ratio: 1;
    }
  }
  &-element {
    box-shadow: 0 2px 2px 2px $grey-400
  }
  &-element:hover {
    border-color: $blue-primary;
    box-shadow: 0 2px 2px 2px $light-blue-400
  }
  &-title {
    font-weight: bold;
    font-size: 16px;
    line-height: normal;
    margin-bottom: .5rem;
    &-custom-examples {
      line-height: normal;
    }
  }
  &-subtitle {
    font-size: 13px;
  }
}
.option-element-template {
  &-closed {
    aspect-ratio: 1;
    min-width: unset;
  }
  &-opened {
    aspect-ratio: unset;
    min-width: 600px;
  }
}
.input-group {
  margin-top: -18px;
}
.import-section-enter-active,
.import-section-leave-active {
  transition: opacity 0.5s ease;
}

.import-section-enter-from,
.import-section-leave-to {
  opacity: 0;
}
.import {
  &-container {
    display: flex;
    flex-direction: column;
  }
  &-section {
    &-toggle {
      width: fit-content;
      cursor: pointer;
      &-icon {
        color: $blue-primary;
        font-size: 35px;
      }
    }
    &-query-string {
      &-syntax-method {
        margin-left: 8px;
      }
      &-cta {
        max-width: fit-content;
      }
    }
  }
}
.query-syntax-btn-group {
  display: flex;
  width: fit-content;
  border-radius: $length-med-xs;
  & > button {
    width: max-content;
    padding: 0 $length-small-xl;
  }
}
.upload {
  &-file-container {
    border: $length-small-xs dashed $grey-800;
    border-radius: $length-small-l;
    padding: $length-small-l $length-med-xs;
    cursor: pointer;
  }
  &-file-element {
    align-self: center;
  }
}
.upload-icon {
  color: $blue-primary;
  border: $length-small-s solid $blue-primary;
  padding: $length-small-l;
  border-radius: 50%;
  margin-right: $length-med-xs;
}
.drag-drop-text {
  text-align: center;
  &-import-file {
    color: $blue-primary;
    text-decoration: underline $blue-primary;
  }
}
.query-syntax-btn {
  min-width: unset;
  border-radius: $length-med-xs;
}
.btn {
  background-color: $grey-800;
  min-height: $length-large-xs;
  text-transform: capitalize;
  font-size: $font-size-small;
  color: $grey-800;
  &-primary {
    color: $bg-primary;
    max-width: $length-large-xs;
  }
}
.or-element {
  font-weight: bold;
  text-decoration: underline;
  color: $blue-primary;
  text-align: center;
  margin-bottom: $length-small-l;
}
#hr-import-section {
  border: .5px solid $light-blue-600;
  margin-top: $length-med-xl;
}
</style>



// WEBPACK FOOTER //
// src/components/mainMenu/NewSearchModal.vue